import * as styles from './index.module.scss';
import {
  AutoComplete,
  Button,
  DatePicker,
  Form,
  FormProps,
  Input,
  Modal,
  Radio,
  message,
  Flex,
} from 'antd';
import { MaskedInput } from 'antd-mask-input';
import { AxiosError } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { GeoPoint, JobFor, jobForDeliveryCenter, Organization } from '../../../ types';
import { StagesStore } from '../../../../DataSource/Stores/Cabinet/StagesStore';
import { WorkspacesStore } from '../../../../DataSource/Stores/Cabinet/WorkspacesStore';
import { OrganizationsStore } from '../../../../DataSource/Stores/Site/OrganizationsStore';
import Icon from '../../../ui/Icon';

type FieldType = {
  jobFor: JobFor;
  town: string;
  from: string;
  geoPointFrom: GeoPoint;
  to: string;
  geoPointTo: GeoPoint;
  datetime: Dayjs;
  instruction: string;
  contactsFrom: Contact[];
  contactsTo: Contact[];
  paymentType: string;
  cost?: number;
};

type Contact = {
  name: string;
  phone: string;
};

interface IAddress {
  value: string;
  contactData: Contact[];
  geoPoint: GeoPoint | null;
}

interface IProps {
  open: boolean;
  onCancel: () => void;
  mode?: 'add' | 'edit';
  _jobFor?: JobFor;
}

const { TextArea } = Input;

const OrderFormModal: React.FC<IProps> = ({ open, onCancel, mode = 'add', _jobFor }) => {
  const { id: workspaceId } = useParams();
  const location = useLocation();
  const [form] = Form.useForm();
  const [jobFor, setJobFor] = useState<JobFor>('safiya');
  const [locationFrom, setLocationFrom] = useState<IAddress[]>([]);
  const [paymentType, setPaymentType] = useState<'cash' | 'online' | null>(null);
  const defaultDateTime = dayjs()
    .set('hour', 23)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0);

  useEffect(() => {
    if (!workspaceId) return;

    (async () => {
      await OrganizationsStore.getList(workspaceId);
      if (OrganizationsStore.dataList.length) {
        const addresses: IAddress[] = OrganizationsStore.dataList.map((org: Organization) => ({
          value: `${org.name} ${org.address.fullAddress}`,
          contactData: org.contactData,
          geoPoint: org.address.geoPoint,
        }));
        setLocationFrom(addresses);
      }
    })();
  }, [workspaceId]);

  useEffect(() => {
    if (_jobFor) {
      setJobFor(_jobFor);
      form.setFieldsValue({ jobFor: _jobFor });
    } else {
      if (location.pathname.includes('/sau')) {
        setJobFor('sau');
        form.setFieldsValue({ jobFor: jobFor });
      } else if (location.pathname.includes('/lulu')) {
        setJobFor('lulu');
        form.setFieldsValue({ jobFor: jobFor });
      } else if (location.pathname.includes('/metabody')) {
        setJobFor('metabody');
        form.setFieldsValue({ jobFor: jobFor });
      } else {
        setJobFor('sau');
      }
    }
  }, [location, _jobFor]);

  useEffect(() => {
    if (mode === 'edit') {
      const job = StagesStore.dataJobs?.job;
      const targetDate = dayjs(job?.targetDate);

      console.log(job);

      form.setFieldsValue({
        from: job?.deliveryData?.pickupPoint?.address,
        to: job?.deliveryData?.deliveryPoint?.address,
        datetime: targetDate,
        contactsFrom: job?.deliveryData?.pickupPoint?.contactData.map((contact) => ({
          name: contact.name,
          phone: contact.phone,
        })),
        contactsTo: job?.deliveryData?.deliveryPoint?.contactData.map((contact) => ({
          name: contact.name,
          phone: contact.phone,
        })),
        geoPointFrom: job?.deliveryData?.pickupPoint?.geoPoint || null,
        geoPointTo: job?.deliveryData?.deliveryPoint?.geoPoint || null,
        instruction: job?.jobData?.[0]?.text || '',
        paymentType: job?.deliveryData?.paymentData?.method,
        cost: job?.deliveryData?.paymentData?.amount,
        jobFor: job?.jobFor,
      });
    } else {
      form.resetFields();
      if (_jobFor) {
        setJobFor(_jobFor);
        form.setFieldsValue({ jobFor: _jobFor });
      }
    }
  }, [mode]);

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    if (!workspaceId) return;

    const fullAddressFrom = [values.town ? values.town : '', values.from ? values.from : '']
      .filter(Boolean)
      .join(', ');

    const fullAddressTo = [values.town ? values.town : '', values.to ? values.to : '']
      .filter(Boolean)
      .join(', ');

    const jobData: jobForDeliveryCenter = {
      name: `Доставка на ${values.to}`,
      externalId: {},
      workspaceId,
      jobData: [
        {
          text: values.instruction,
          photoFiles: [],
        },
      ],
      targetDate: values.datetime.toDate(),
      deliveryData: {
        pickupPoint: {
          address: mode === 'edit' ? values.from : fullAddressFrom,
          contactData: values.contactsFrom,
          geoPoint: values.geoPointFrom || null,
        },
        deliveryPoint: {
          address: mode === 'edit' ? values.to : fullAddressTo,
          contactData: values.contactsTo,
          geoPoint: values.geoPointTo || null,
        },
        paymentData: {
          method: values.paymentType,
          amount: values.cost || 0,
        },
      },
      jobFor,
    };

    const _id = StagesStore.dataJobs?.job?._id;

    try {
      if (mode === 'edit') {
        await WorkspacesStore.updateJobForDeliveryCenter(workspaceId, _id ? _id : '', jobData);
        message.info('Job updated successfully');
        StagesStore.setUpdateTable(true);
        form.resetFields();
      } else {
        await WorkspacesStore.addJobForDeliveryCenter(workspaceId, jobData);
        message.info('Job added successfully');
        StagesStore.setUpdateTable(true);
      }

      if (_jobFor) {
        try {
          const res = await WorkspacesStore.getJobsByJobFor.call(workspaceId, _jobFor);
          StagesStore.setDataJobList(res.data.data);
        } catch (e) {
          console.error(e);
        }
      } else {
        await StagesStore.getListJobs(workspaceId);
      }

      form.resetFields();
    } catch (err) {
      if (err instanceof AxiosError && err?.response?.data?.message?.message) {
        return message.error(err?.response?.data?.message?.message);
      }
    }
  };

  const handleSelectFrom = (option: IAddress | IAddress[]) => {
    if (Array.isArray(option)) return;

    if (!Array.isArray(option.contactData) || option.contactData.length === 0) return;

    const contactsFrom = option.contactData.map((contact) => ({
      name: contact.name,
      phone: contact.phone,
    }));

    const geoPointFrom = option.geoPoint;

    form.setFieldsValue({
      contactsFrom,
      geoPointFrom,
    });
  };

  const handleSelectTo = (option: IAddress | IAddress[]) => {
    if (Array.isArray(option)) return;

    if (!Array.isArray(option.contactData) || option.contactData.length === 0) return;

    const contactsTo = option.contactData.map((contact) => ({
      name: contact.name,
      phone: contact.phone,
    }));

    const geoPointTo = option.geoPoint;

    form.setFieldsValue({
      contactsTo,
      geoPointTo,
    });
  };

  const handleCancel = () => {
    onCancel();
    form.resetFields();
  };

  return (
    <Modal footer={false} open={open} onCancel={handleCancel}>
      <Flex vertical gap={16}>
        {mode === 'add' && <p className={styles.title}>новый заказ</p>}

        {mode === 'edit' && <p className={styles.title}>#{StagesStore.dataJobs?.job?.jobId}</p>}

        <Form
          form={form}
          name="basic"
          layout="vertical"
          requiredMark={false}
          autoComplete="off"
          initialValues={{ town: 'Алматы', datetime: defaultDateTime }}
          onFinish={onFinish}
        >
          <Form.Item<FieldType>
            name="jobFor"
            rules={[{ required: true, message: 'Укажите группу' }]}
          >
            <Radio.Group
              disabled={!!_jobFor}
              value={jobFor}
              onChange={(e) => setJobFor(e.target.value)}
            >
              <Radio value="sau">SAU</Radio>
              <Radio value="lulu">LULU</Radio>
              <Radio value="luluopt">LULU Опт</Radio>
              <Radio value="metabody">METABODY</Radio>
            </Radio.Group>
          </Form.Item>

          {mode === 'add' && <div className={styles.status}>создание</div>}

          {mode === 'edit' && <div className={styles.status}>редактирование</div>}

          {mode === 'add' && (
            <Form.Item name="town" label="Город:">
              <Input placeholder="Алматы" value="Алматы" readOnly />
            </Form.Item>
          )}

          <Form.Item<FieldType>
            name="from"
            label="Откуда:"
            rules={[{ required: true, message: 'Укажите адрес' }]}
          >
            <AutoComplete
              placeholder="Введите или выберите адрес.."
              options={locationFrom}
              onSelect={handleSelectFrom}
            />
          </Form.Item>

          <Form.Item<FieldType> name="geoPointFrom" style={{ display: 'none' }}>
            <Input />
          </Form.Item>

          <Form.Item<FieldType>
            name="to"
            label="Куда:"
            rules={[{ required: true, message: 'Укажите адрес' }]}
          >
            <AutoComplete
              options={locationFrom}
              placeholder="Введите или выберите адрес.."
              onSelect={handleSelectTo}
            />
          </Form.Item>

          <Form.Item<FieldType> name="geoPointTo" style={{ display: 'none' }}>
            <Input />
          </Form.Item>

          <Form.Item<FieldType>
            name="datetime"
            label="Дата и время доставки:"
            rules={[{ required: true, message: 'Заполните поле' }]}
          >
            <DatePicker
              placeholder="Выберите дату"
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>

          <Form.Item<FieldType> name="instruction" label="Инструкция для курьера:">
            <TextArea placeholder="Введите описание..." autoSize={{ minRows: 2, maxRows: 6 }} />
          </Form.Item>

          <p className={styles.label}>Контакты в точке забора:</p>
          <Form.List
            name="contactsFrom"
            rules={[
              {
                validator: async (_, contactsFrom) => {
                  if (!contactsFrom || contactsFrom.length === 0) {
                    return Promise.reject(new Error('Вы должны добавить хотя бы один контакт'));
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'phone']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Требуется телефон!' },
                        {
                          pattern: /^[0-9+() -]*$/,
                          message: 'Введите корректный номер телефона',
                        },
                      ]}
                    >
                      <MaskedInput mask="+7 (000) 000-0000" />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      style={{ marginLeft: 8, marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Требуется указать имя' },
                        {
                          pattern: /^[a-zA-Zа-яА-ЯёЁ\s]+$/,
                          message: 'Имя может содержать только буквы!',
                        },
                      ]}
                    >
                      <Input placeholder="Имя" />
                    </Form.Item>

                    <Button
                      icon={<Icon slug="minus" size={18} color="#959595" />}
                      shape="default"
                      type="link"
                      style={{ width: 32, marginLeft: 8 }}
                      onClick={() => remove(name)}
                    ></Button>
                  </div>
                ))}

                <Form.Item>
                  <Button type="default" onClick={() => add()} block>
                    + Добавить контакт
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>

          <p className={styles.label}>Контакты в точке Доставки:</p>
          <Form.List
            name="contactsTo"
            rules={[
              {
                validator: async (_, contactsTo) => {
                  if (!contactsTo || contactsTo.length === 0) {
                    return Promise.reject(new Error('Вы должны добавить хотя бы один контакт!'));
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'phone']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Требуется телефон!' },
                        {
                          pattern: /^[0-9+() -]*$/,
                          message: 'Введите корректный номер телефона',
                        },
                      ]}
                    >
                      <MaskedInput mask="+7 (000) 000-0000" />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      style={{ marginLeft: 8, marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Требуется указать имя' },
                        {
                          pattern: /^[a-zA-Zа-яА-ЯёЁ\s]+$/,
                          message: 'Имя может содержать только буквы!',
                        },
                      ]}
                    >
                      <Input placeholder="Имя" />
                    </Form.Item>

                    <Button
                      icon={<Icon slug="minus" size={18} color="#959595" />}
                      shape="default"
                      type="link"
                      style={{ width: 32, marginLeft: 8 }}
                      onClick={() => remove(name)}
                    ></Button>
                  </div>
                ))}

                <Form.Item>
                  <Button type="default" onClick={() => add()} block>
                    + Добавить контакт
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item<FieldType>
            name="paymentType"
            label="Способ оплаты:"
            rules={[{ required: true, message: 'Пожалуйста, выберите тип оплаты' }]}
          >
            <Radio.Group onChange={(e) => setPaymentType(e.target.value)}>
              <Radio value={'cash'}>Наличные</Radio>
              <Radio value={'online'}>Оплачено</Radio>
            </Radio.Group>
          </Form.Item>

          {paymentType !== 'online' && (
            <>
              <Form.Item<FieldType>
                name="cost"
                label="Сумма, ₸"
                rules={[
                  {
                    type: 'number',
                    message: 'Сумма должна быть числом!',
                    transform: (value) => (value ? Number(value) : value),
                  },
                ]}
              >
                <Input
                  placeholder="Введите сумму..."
                  prefix="₸"
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                  }}
                />
              </Form.Item>
            </>
          )}

          <Button
            htmlType="submit"
            type="primary"
            loading={WorkspacesStore.addloading}
            style={{ width: '100%' }}
          >
            {mode === 'edit' ? 'Обновить' : 'Добавить'}
          </Button>
        </Form>
      </Flex>
    </Modal>
  );
};

export default observer(OrderFormModal);
